/**
 * Compositor WebGL2 del preview.
 *
 * Dos programas:
 *  - effectProgram: 1 textura → adjust + LUT (uso 99% del tiempo)
 *  - transitionProgram: 2 texturas → blend con `id` de transición xfade
 *    (durante la región de solape entre clip A y clip B)
 *
 * Los uniforms de adjust/LUT no se aplican durante la transición — v1 acepta
 * que durante el solape los efectos del clip queden "pausados" para evitar
 * el coste de 2 framebuffers extra. (Pasable porque las transiciones suelen
 * durar 0.5-1s sobre clips largos.)
 */
import { createProgram } from "./webgl";
import { fetchCubeLut, uploadLut3D } from "./lut";
import { FRAG, VERT } from "./shaders";
import { TRANSITION_FRAG, TRANSITION_VERT } from "./transitionShader";
import { DynamicTransitionCompositor } from "./dynamicTransitionCompositor";
import { DYNAMIC_TRANSITIONS, type DynamicTransition } from "./transitionLibrary";

/** Map id → entry para lookup rápido. */
const DYNAMIC_BY_ID: Record<string, DynamicTransition> = Object.fromEntries(
  DYNAMIC_TRANSITIONS.map((t) => [t.id, t]),
);

export function isDynamicTransition(id: string | null | undefined): boolean {
  return !!id && id in DYNAMIC_BY_ID;
}
export function getDynamicTransition(id: string): DynamicTransition | null {
  return DYNAMIC_BY_ID[id] || null;
}

export interface RenderEffects {
  exposure: number;
  contrast: number;
  saturation: number;
  temperature: number;
  lutFile: string | null;
  lutIntensity: number;
}

export const DEFAULT_EFFECTS: RenderEffects = {
  exposure: 0, contrast: 1, saturation: 1, temperature: 0,
  lutFile: null, lutIntensity: 1,
};

export function effectsFromClip(effects: any[] | undefined): RenderEffects {
  const r = { ...DEFAULT_EFFECTS };
  for (const e of effects || []) {
    if (e.enabled === false) continue;
    if (e.type === "adjust") {
      r.exposure += Number(e.exposure) || 0;
      if (e.contrast !== undefined) r.contrast *= Number(e.contrast);
      if (e.saturation !== undefined) r.saturation *= Number(e.saturation);
      r.temperature += Number(e.temperature) || 0;
    } else if (e.type === "lut" && e.file) {
      r.lutFile = e.file;
      if (e.intensity !== undefined) r.lutIntensity = Number(e.intensity);
    }
  }
  return r;
}

export class Compositor {
  readonly gl: WebGL2RenderingContext;
  private effectProgram: WebGLProgram;
  private trProgram: WebGLProgram;
  private videoTexA: WebGLTexture;
  private videoTexB: WebGLTexture;
  private lutCache = new Map<string, WebGLTexture>();
  private loadingLut = new Set<string>();
  private uniE: Record<string, WebGLUniformLocation | null> = {};
  private uniT: Record<string, WebGLUniformLocation | null> = {};
  private dyn: DynamicTransitionCompositor;
  private vbo: WebGLBuffer;
  // Cachear location del attrib `a_pos` por programa (depende del compilador GLSL)
  private aPosEffect: number = -1;
  private aPosTr: number = -1;

  /** Re-setup completo del VBO + vertex attrib para el programa actual.
   *  Llamar SIEMPRE después de useProgram(): el compositor dinámico cambia
   *  el binding y rompe los draws posteriores si no se restaura. */
  private bindQuadFor(aPos: number) {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  }

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl2", {
      premultipliedAlpha: false, alpha: false, antialias: false,
    });
    if (!gl) throw new Error("WebGL2 no está disponible en este navegador");
    this.gl = gl;

    // VBO compartido (quad fullscreen)
    const buf = gl.createBuffer()!;
    this.vbo = buf;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,  1, -1,  -1, 1,  1, 1,
    ]), gl.STATIC_DRAW);

    // Programa 1: efectos + LUT
    this.effectProgram = createProgram(gl, VERT, FRAG);
    gl.useProgram(this.effectProgram);
    this.aPosEffect = gl.getAttribLocation(this.effectProgram, "a_pos");
    gl.enableVertexAttribArray(this.aPosEffect);
    gl.vertexAttribPointer(this.aPosEffect, 2, gl.FLOAT, false, 0, 0);
    for (const name of [
      "u_video", "u_lut", "u_useLut", "u_lutIntensity",
      "u_exposure", "u_contrast", "u_saturation", "u_temperature",
    ]) this.uniE[name] = gl.getUniformLocation(this.effectProgram, name);
    gl.uniform1i(this.uniE.u_video!, 0);
    gl.uniform1i(this.uniE.u_lut!, 1);

    // Programa 2: transiciones (2 inputs)
    this.trProgram = createProgram(gl, TRANSITION_VERT, TRANSITION_FRAG);
    gl.useProgram(this.trProgram);
    this.aPosTr = gl.getAttribLocation(this.trProgram, "a_pos");
    gl.enableVertexAttribArray(this.aPosTr);
    gl.vertexAttribPointer(this.aPosTr, 2, gl.FLOAT, false, 0, 0);
    for (const name of ["u_videoA", "u_videoB", "u_transition", "u_progress"]) {
      this.uniT[name] = gl.getUniformLocation(this.trProgram, name);
    }
    gl.uniform1i(this.uniT.u_videoA!, 0);
    gl.uniform1i(this.uniT.u_videoB!, 2);

    // Texturas
    this.videoTexA = this._makeVideoTex(gl, 0);
    this.videoTexB = this._makeVideoTex(gl, 2);

    // Compositor dinámico (gl-transitions)
    this.dyn = new DynamicTransitionCompositor(gl, this.vbo);
  }

  private _makeVideoTex(gl: WebGL2RenderingContext, unit: number): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  preloadLut(file: string): void {
    if (this.lutCache.has(file) || this.loadingLut.has(file)) return;
    this.loadingLut.add(file);
    fetchCubeLut(`/luts/${file}`)
      .then((lut) => { this.lutCache.set(file, uploadLut3D(this.gl, lut)); })
      .catch((err) => console.error("LUT load failed", file, err))
      .finally(() => { this.loadingLut.delete(file); });
  }

  private _sourceDims(s: HTMLVideoElement | HTMLImageElement): { w: number; h: number; ready: boolean } {
    if (s instanceof HTMLVideoElement) {
      return { w: s.videoWidth, h: s.videoHeight, ready: s.readyState >= 2 && s.videoWidth > 0 };
    }
    return { w: s.naturalWidth, h: s.naturalHeight, ready: s.complete && s.naturalWidth > 0 };
  }

  render(source: HTMLVideoElement | HTMLImageElement, e: RenderEffects): void {
    const gl = this.gl;
    const canvas = gl.canvas as HTMLCanvasElement;
    const dims = this._sourceDims(source);
    if (!dims.ready) { this.clear(); return; }
    if (canvas.width !== dims.w) canvas.width = dims.w;
    if (canvas.height !== dims.h) canvas.height = dims.h;

    gl.useProgram(this.effectProgram);
    // CRÍTICO: re-bindear el quad para este programa. Cualquier
    // useProgram previo (renderTransition, dyn.render) deja el atributo
    // configurado para OTRO programa.
    this.bindQuadFor(this.aPosEffect);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexA);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

    gl.uniform1f(this.uniE.u_exposure!, e.exposure);
    gl.uniform1f(this.uniE.u_contrast!, e.contrast);
    gl.uniform1f(this.uniE.u_saturation!, e.saturation);
    gl.uniform1f(this.uniE.u_temperature!, e.temperature);

    let useLut = false;
    if (e.lutFile && e.lutFile !== "") {
      const tex = this.lutCache.get(e.lutFile);
      if (tex) {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_3D, tex);
        useLut = true;
        gl.uniform1f(this.uniE.u_lutIntensity!, e.lutIntensity);
      } else {
        this.preloadLut(e.lutFile);
      }
    }
    gl.uniform1i(this.uniE.u_useLut!, useLut ? 1 : 0);

    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /**
   * Renderiza la transición entre A (clip saliente) y B (entrante).
   * Acepta:
   *  - id numérico (xfade builtin / shader compartido)
   *  - id string (gl-transitions dinámico)
   */
  renderTransition(
    srcA: HTMLVideoElement | HTMLImageElement,
    srcB: HTMLVideoElement | HTMLImageElement,
    transitionId: number | string,
    progress: number,
  ): void {
    const gl = this.gl;
    const canvas = gl.canvas as HTMLCanvasElement;
    const dimsA = this._sourceDims(srcA);
    const dimsB = this._sourceDims(srcB);
    if (!dimsA.ready && !dimsB.ready) { this.clear(); return; }
    const target = dimsA.ready ? dimsA : dimsB;
    if (canvas.width !== target.w) canvas.width = target.w;
    if (canvas.height !== target.h) canvas.height = target.h;

    // Subir texturas (siempre, mismo VBO)
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexA);
    if (dimsA.ready) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, srcA);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexB);
    if (dimsB.ready) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, srcB);

    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Path dinámico (gl-transitions)
    if (typeof transitionId === "string") {
      const def = getDynamicTransition(transitionId);
      if (def) {
        const ok = this.dyn.render(def.id, def.glsl, progress);
        if (ok) return;
      }
      // fallback: usar shader legacy con id=0 (fade)
    }

    // Path legacy (shader compartido con ~94 transiciones)
    gl.useProgram(this.trProgram);
    this.bindQuadFor(this.aPosTr);
    gl.uniform1i(this.uniT.u_transition!, (typeof transitionId === "number" ? transitionId : 0) | 0);
    gl.uniform1f(this.uniT.u_progress!, Math.max(0, Math.min(1, progress)));
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /** Acceso al compositor dinámico para los thumbnails del panel. */
  dynamicCompositor(): DynamicTransitionCompositor { return this.dyn; }

  clear(): void {
    const gl = this.gl;
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteTexture(this.videoTexA);
    gl.deleteTexture(this.videoTexB);
    for (const tex of this.lutCache.values()) gl.deleteTexture(tex);
    this.lutCache.clear();
    gl.deleteProgram(this.effectProgram);
    gl.deleteProgram(this.trProgram);
    this.dyn.dispose();
  }
}
